import {
	OnboardingTourConnect,
	OnboardingTourState,
	OnboardingTourVariant,
} from "@/pages/Private/redux/onboardingTour/schema";

import { STEP_ORDER, TourStepId } from "./types";

/**
 * True once the user has a usable sender: a connected LinkedIn profile OR a
 * connected email. Basis for showing the CONNECT step as already done (PREC-3719
 * follow-up: pre-onboarding companies).
 */
export const isSenderConnected = (connect: OnboardingTourConnect): boolean =>
	connect.linkedinConnected || connect.emailConnected;

/**
 * The tour steps the current user should actually walk through — `STEP_ORDER`
 * minus the steps that don't apply to them:
 *
 * - CREATE_KNOWLEDGE is dropped once the Knowledge Base already has content
 *   (`readiness.knowledgeDbReady`). Companies created before onboarding existed
 *   never seeded the KB-setup drafts, so the chat can never resolve — and a
 *   company that already has a KB has nothing to create.
 * - CONNECT and LANGUAGE are dropped for users without a sender licence
 *   (`linkedinAllowed === false`): they can neither connect a sender nor set
 *   writing languages, so both screens are dead ends.
 * - EXTENSION is dropped while the EXTENSION_PROMOTION feature flag is off, and
 *   once the user already installed the browser extension (PREC-3815) — there is
 *   nothing left to promote.
 * - SCORING_ACTIVATE is dropped for the INVITED "Welcome tour": it corrects and
 *   re-activates the workspace's shared scoring, which an invited user must not
 *   do — they only get shown how the configuration reads.
 */
export const visibleTourSteps = (
	state: OnboardingTourState | null,
	linkedinAllowed: boolean,
	extensionPromoEnabled: boolean
): TourStepId[] =>
	STEP_ORDER.filter((step) => {
		if (step === TourStepId.CREATE_KNOWLEDGE && state?.readiness.knowledgeDbReady) {
			return false;
		}

		if (
			step === TourStepId.EXTENSION &&
			(!extensionPromoEnabled || state?.connect.extensionInstalled)
		) {
			return false;
		}

		if (step === TourStepId.SCORING_ACTIVATE && state?.variant === OnboardingTourVariant.INVITED) {
			return false;
		}

		if ((step === TourStepId.CONNECT || step === TourStepId.LANGUAGE) && !linkedinAllowed) {
			return false;
		}

		return true;
	});

/**
 * Whether a step counts as done for progress purposes — completed in the tour,
 * or already satisfied outside it (a connected sender for CONNECT, an installed
 * browser extension for EXTENSION).
 */
export const isTourStepComplete = (state: OnboardingTourState, step: TourStepId): boolean =>
	state.completedSteps.includes(step) ||
	(step === TourStepId.CONNECT && isSenderConnected(state.connect)) ||
	(step === TourStepId.EXTENSION && Boolean(state.connect.extensionInstalled));
